import React, { useEffect, useState } from "react";
import {
  Box,
  Tooltip,
  IconButton,
  TextField,
  Typography,
  Grid,
} from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";

import AddButton from "@Components/Buttons/AddButton";
import LoadingComponent from "@Components/LoadingComponent/LoadingComponent";
import ListingPageTemplate from "@Pages/ListingPageTemplate/ListingPageTemplate";
import themeCreator from "@Themes/themeCreator";
import useDeleteItem from "@Hooks/useDeleteItem";
import usePostItem from "@Hooks/usePostItem";
import useGetAll from "@Hooks/useGetAll";
import { Delete } from "@mui/icons-material";
import GenericPopup from "@Components/Popup/Popup";
import { useDispatch } from "react-redux";
import {
  validateData,
  validateField,
  validateUniqueness,
} from "@Utils/Validations/HelperFunctions/FormValidationFunctions";
import NoteAddRoundedIcon from "@mui/icons-material/NoteAddRounded";
import { DeletePopup } from "@Components/Popup/DeletePopup";
import { AgentDocumentPopup } from "../AgentDocumentPopup/AgentDocumentPopup";
import {
  CLEARANCE_AGENT_DOCUMENTS_URL,
  VENDOR_DOCUMENTS_URL,
} from "@API/APIRoutes";
import axios from "axios";
import * as DefaultRoutes from "@Routes/DefaultRoutes";
import InfoRoundedIcon from "@mui/icons-material/InfoRounded";
import LinkIcon from "@mui/icons-material/Link";
import { LinkPlannerPopup } from "../LinkPlannerPopup/LinkPlannerPopup";
import { encryptId } from "../../../Utils/SecurityFunctions";

function GenericItemsListing({
  items_URL,
  SAVE_Item,
  DELETE_Item,
  title,
  name = "Item",
  icon,
  attributes, // array of objects: [{ key: "name", label: "Name", flex: 1 }, ...]
  validationSchema,
  customFormForAddtion, // must contain (formContetn and function to call after submission)
  isViewerOnly,
  uniqueId = "id",
  clearData,
  isClearanceAgent = false,
  isVendor = false,
  isClearancePlanner = false,
}) {
  const theme = themeCreator();

  const [data, setData] = useState([]);
  const [showDeletePopup, setShowDeletePopup] = useState(false);
  const [showAddItemPopup, setShowAddItemPopup] = useState(false);
  const [idToBeDeleted, setIdToBeDeleted] = useState(null);
  const [newItem, setNewItem] = useState({});
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { loading: insertDocumentLoading, postItem: insertDocument } =
    usePostItem();
  const [showAgenttDocumentPopup, setShowAgenttDocumentPopup] = useState(false);
  const [showLinkPlannerPopup, setShowLinkPlannerPopup] = useState(false);

  const [selectedAgentId, setSelectedAgentId] = useState(null);
  const [selectedPlannerId, setSelectedPlannerId] = useState(null);
  const { loading: deleteItemLoading, deleteItem } = useDeleteItem();
  const { loading: PostItemLoading, postItem } = usePostItem();
  const [errors, setErrors] = useState({});
  const { loading: getAllLoading, getAll } = useGetAll();
  const closeShowAgentDocumentPopup = () => setShowAgenttDocumentPopup(false);
  const closeLinkPlannerPopup = () => setShowLinkPlannerPopup(false);

  const openShowAgentDocumentPopup = (rowId) => {
    setSelectedAgentId(rowId);
    setShowAgenttDocumentPopup(true);
  };
  const openShowLinkPlannerPopup = (rowId) => {
    setSelectedPlannerId(rowId);
    setShowLinkPlannerPopup(true);
  };

  // Initialize newItem state based on attributes (excluding the attribute with key "id")
  useEffect(() => {
    const initialNewItem = {};
    attributes.forEach((attr) => {
      if (attr.key !== "id") {
        initialNewItem[attr.key] = "";
      }
    });
    setNewItem(initialNewItem);
  }, [attributes]);

  const ConfirmLinkPlanner = async (document, file) => {
    // const response = await insertDocument(
    //   CLEARANCE_AGENT_DOCUMENTS_URL,
    //   documentData,
    //   closeShowAgentDocumentPopup,
    //   `Document Inserted Successfully!`,
    //   `Failed to insert Document!`
    // )

    closeShowAgentDocumentPopup();
  };

  const ConfirmInsertAgentDocument = async (document, file) => {
    const documentData = isVendor
      ? { ...document, vendorId: selectedAgentId }
      : { ...document, agentId: selectedAgentId };
    const response = await insertDocument(
      isVendor
        ? VENDOR_DOCUMENTS_URL(selectedAgentId)
        : CLEARANCE_AGENT_DOCUMENTS_URL,
      documentData,
      closeShowAgentDocumentPopup,
      `Document Inserted Successfully!`,
      `Failed to insert Document!`
    );
    if (response.preSignedUrl != null) {
      const { preSignedUrl, objectKey } = response;

      // Upload file to Minio
      await axios.put(preSignedUrl, file, {
        headers: {
          "Content-Type": "application/pdf",
        },
      });

      closeShowAgentDocumentPopup();
    }
  };
  const closePopup = () => setShowDeletePopup(false);
  const closeAddItemPopup = () => {
    setErrors({});
    setShowAddItemPopup(false);
    resetNewItem();
    if (clearData) {
      clearData();
    }
  };

  const openDeletePopup = (itemId) => {
    setIdToBeDeleted(itemId);
    setShowDeletePopup(true);
  };
  useEffect(() => {
    getAll(items_URL, `Failed to fetch ${title}`)
      .then((fetchedData) => {
        fetchedData.reverse();
        const mappedData = fetchedData.map((item) => {
          return { ...item, id: item[uniqueId] };
        });
        setData(mappedData);
      })
      .catch((error) => console.error(error));
  }, []);

  if (getAllLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height={theme.pageHeight}
      >
        <LoadingComponent />
      </Box>
    );
  }

  const openAddItemPopup = () => {
    setShowAddItemPopup(true);
  };

  const handleTextFieldChange = (attrKey) => (event) => {
    setNewItem((prev) => ({ ...prev, [attrKey]: event.target.value }));
    setErrors({ ...errors, [attrKey]: "" });
  };

  const resetNewItem = () => {
    const emptyObj = {};
    attributes.forEach((attr) => {
      if (attr.key !== "id") {
        emptyObj[attr.key] = "";
      }
    });
    setNewItem(emptyObj);
  };
  const trimData = (data) => {
    return Object.fromEntries(
      Object.entries(data).map(([key, value]) =>
        typeof value === "string" ? [key, value.trim()] : [key, value]
      )
    );
  };
  const handleAddItem = () => {
    if (customFormForAddtion) {
      customFormForAddtion
        .formButton()
        .then((newUser) => {
          if (newUser) {
            postItem(
              SAVE_Item,
              newUser,
              postCallBack,
              `${name} Added Successfully!`,
              `Failed to add ${name}`
            );
          }
        })

        .catch((error) => console.error(error));
    } else {
      const trimmedItem = trimData(newItem);
      setNewItem(trimmedItem);
      const errors = validateData(trimmedItem, validationSchema);
      setErrors(errors);
      if (Object.keys(errors).length === 0) {
        const isUnique = validateUniqueness(
          data,
          trimmedItem,
          name,
          closeAddItemPopup,
          dispatch
        );
        if (isUnique) {
          postItem(
            SAVE_Item,
            newItem,
            postCallBack,
            `${name} Added Successfully!`,
            `Failed to add ${name}`
          );
        }
      }
    }
  };

  const postCallBack = (addedItem) => {
    setData((prevData) => [
      ...prevData,
      { ...addedItem, id: addedItem[uniqueId] },
    ]);
    resetNewItem();
    closeAddItemPopup();
  };

  const confirmDeleteCallBack = (deletedItemId) => {
    setData((prevData) => prevData.filter((item) => item.id !== deletedItemId));
    closePopup();
  };

  const ConfirmDelete = (itemId) => {
    deleteItem(
      itemId,
      confirmDeleteCallBack,
      DELETE_Item,
      `${name} Deleted Successfully!`,
      `Failed to delete ${name}`
    );
  };

  // Create columns using attributes' key, label, and flex values
  const columns = [
    ...attributes.map((attr) => ({
      field: attr.key,
      headerName: attr.label,
      flex: attr.flex || 2,
      minWidth: 100,
    })),
    ...(!isViewerOnly
      ? [
          {
            field: "actions",
            headerName: "Actions",
            sortable: false,
            filterable: false,
            flex: 1,
            minWidth: 120,
            renderCell: (params) => (
              <ThemeProvider theme={theme}>
                <Box sx={{ display: "flex", gap: 0.5 }}>
                  {/* Insert Document Button */}
                  {(isClearanceAgent || isVendor) && (
                    <Tooltip title="Insert Document">
                      <IconButton
                        onClick={() =>
                          openShowAgentDocumentPopup(params.row.id)
                        }
                        size="small"
                      >
                        <NoteAddRoundedIcon
                          style={{ color: theme.palette.primary.main }}
                        />
                      </IconButton>
                    </Tooltip>
                  )}

                  {isClearancePlanner && (
                    <Tooltip title="Link Planner With Email">
                      <IconButton
                        onClick={() => openShowLinkPlannerPopup(params.row.id)}
                        size="small"
                      >
                        <LinkIcon
                          style={{ color: theme.palette.primary.main }}
                        />
                      </IconButton>
                    </Tooltip>
                  )}

                  {/* Delete Button */}
                  <Tooltip title="Delete">
                    <IconButton
                      onClick={() => openDeletePopup(params.row.id)}
                      size="small"
                    >
                      <Delete style={{ color: theme.palette.red.main }} />
                    </IconButton>
                  </Tooltip>
                  {/* preview agent documents Button */}
                  {(isClearanceAgent || isVendor) && (
                    <Tooltip title={isVendor ? "Preview Vendor Documents" : "Preview Agent Portfolio"}>
                      <IconButton
                        color="info"
                        onClick={() =>
                          navigate(
                            (isVendor
                              ? DefaultRoutes.vendorPortfolio.replace(
                                  ":vendorId",
                                  encryptId(params.row.id)
                                )
                              : DefaultRoutes.agentPortfolio.replace(
                                  ":agentId",
                                  encryptId(params.row.id)
                                )),
                            {
                              state: isVendor
                                ? { entityName: params.row.vendorName }
                                : { agentName: params.row.agentName },
                            }
                          )
                        }
                        size="small"
                      >
                        <InfoRoundedIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              </ThemeProvider>
            ),
          },
        ]
      : []),
  ];

  const itemsTableProps = {
    data,
    columns,
    disableRowSelectionOnClick: true,
    filtersEnable: true,
    searchBarEnable: true,
    disableColumnMenu: true,
  };

  const handleOnBlur = async (field, value) => {
    validateField(
      field,
      value,
      setNewItem,
      newItem,
      validationSchema,
      setErrors
    );
  };

  const addItemPopup = (
    <GenericPopup
      icon={icon}
      open={showAddItemPopup}
      title={`Add ${name}`}
      content={
        customFormForAddtion ? (
          customFormForAddtion.formContent
        ) : (
          <Box noValidate autoComplete="off">
            {attributes
              .filter((attr) => attr.key !== "id")
              .map((attr) => (
                <Grid item xs={12}>
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    {attr.label}
                  </Typography>
                  <TextField
                    key={attr.key}
                    variant="outlined"
                    fullWidth
                    value={newItem[attr.key] || ""}
                    onChange={handleTextFieldChange(attr.key)}
                    onBlur={(e) => handleOnBlur(attr.key, e.target.value)}
                    error={!!errors[attr.key]}
                    helperText={errors[attr.key] || ""}
                    placeholder={attr.label}
                  />
                </Grid>
              ))}
          </Box>
        )
      }
      buttons={[
        {
          text: "Cancel",
          onClick: closeAddItemPopup,
          color: "secondary",
          variant: "outlined",
        },
        {
          text: "Add",
          onClick: handleAddItem,
          color: "primary",
          variant: "contained",
          loading: PostItemLoading,
        },
      ]}
    />
  );
  return (
    <Box>
      <ListingPageTemplate
        title={title}
        PageIcon={icon}
        iconColor={theme.palette.primary.main}
        buttons={
          !isViewerOnly
            ? [
                <AddButton
                  key="add-button"
                  onClick={openAddItemPopup}
                  ButtonText={`Add ${name}`}
                />,
              ]
            : []
        }
        tableProps={itemsTableProps}
      />
      {DeletePopup(
        name,
        showDeletePopup,
        closePopup,
        ConfirmDelete,
        idToBeDeleted,
        deleteItemLoading
      )}
      {
        <AgentDocumentPopup
          showPopup={showAgenttDocumentPopup}
          closePopup={closeShowAgentDocumentPopup}
          Confirm={ConfirmInsertAgentDocument}
          loading={insertDocumentLoading}
          entityLabel={isVendor ? "Vendor" : "Agent"}
        />
      }
      {
        <LinkPlannerPopup
          selectedPlannerId={selectedPlannerId}
          showPopup={showLinkPlannerPopup}
          closePopup={closeLinkPlannerPopup}
        />
      }
      {addItemPopup}
    </Box>
  );
}

export default GenericItemsListing;
